import React from 'react';
import clsx from 'clsx';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import SpeedIcon from '../icons/SpeedIcon';
import PowerIcon from '../icons/PowerIcon';
import AgilityIcon from '../icons/AgilityIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import { COLOR_BLACK, COLOR_WHITE, COLOR_TEXT } from '../styles/colors';
import EventRating from '../types/EventRating';
import ProCombineRating from '../types/ProCombineRating';
import { addSpaceBetweenNumAndAlpha, getMostRecentDataByProperty } from '../services/player';
import CircleBadge from '../atoms/CircleBadge';
import NFLCombine from '../types/NFLCombine';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import HandIcon from '../icons/HandIcon';
import ArmIcon from '../icons/ArmIcon';
import DistanceIcon from '../icons/DistanceIcon';
import { inchesToFeetAndInches } from '../services/converter';
import Player from '../types/Player';
import NFLTeam from '../types/NFLTeam';

interface UpdatedPlayerProCombineRatingProps {
  className?: string;
  proCombineRatings?: ProCombineRating[];
  nflCombines: NFLCombine[];
  selectedPosition?: string;
  player?: Player;
}

const useStyles = makeStyles((theme:Theme) => ({
  playerProCombineRating: {},
  headerName: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: '100%',
    fontWeight: 700,
  },
  headerIcon: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '44px',
    height: '44px',
    marginRight: theme.spacing(1.5),
    borderRadius: '50%',
    border: `2px solid ${COLOR_BLACK}`,
    backgroundColor: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_WHITE,
    
    [theme.breakpoints.down('md')]: {
      width: 34,
      height: 34,
      fontSize: theme.typography.pxToRem(12),
    },
  },
  headerText: {
    flexGrow: 1,
  },
  gridContainer: {
    alignItems: 'center',
  },
  rowContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    
    [theme.breakpoints.down('md')]: {
      display: 'inline-flex',
    },
  },
  gridIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    padding: 0,
    marginLeft: 'auto',
  },
  progressLineBar: {
    backgroundColor: COLOR_TEXT,
  },
  badgeWrap: {
    marginRight: 10,
    width: '45px',
  },
  badgeVal: {
    color: COLOR_BLACK,
  },
}), { name: UpdatedPlayerProCombineRating.name });

const BETTER_THAN_TOOLTIP_TEXT = 'Better than NFL Draft position';

function aggregateAllEventsOfType (eventType:string, proCombineRatings: ProCombineRating[]):EventRating[] {
  return proCombineRatings
    // @ts-ignore
    .flatMap((combine:ProCombineRating) => combine[eventType] as EventRating[] || []);
}

function getTheBestEvent (events:EventRating[]):EventRating {
  const sortedEvents = !!events?.length
    ? events
      .sort((firstEvent:EventRating, secondEvent:EventRating) => {
        if (firstEvent?.percentile > secondEvent?.percentile) return -1;
        if (firstEvent?.percentile < secondEvent?.percentile) return 1;
        return 0;
      })
    : [];

  const firstEvent = sortedEvents[0];
  const anotherEventWithTheSamePercentileIndex = sortedEvents?.length > 1
    ? sortedEvents.slice(1).findIndex((event:EventRating) => {
        return event.percentile === firstEvent.percentile && event.name !== firstEvent.name;
      }) + 1
    : 0;

  // show 2 event names if their results are the same (edge case)
  if (anotherEventWithTheSamePercentileIndex > 0) {
    return {
      name: `${firstEvent?.name}/${sortedEvents[anotherEventWithTheSamePercentileIndex]?.name}`,
      percentile: firstEvent.percentile,
    } as EventRating;
  }

  return firstEvent;
}

export default function UpdatedPlayerProCombineRating (props:UpdatedPlayerProCombineRatingProps) {
  const {
    className,
    proCombineRatings,
    nflCombines,
    selectedPosition,
    player
  } = props;
  const classes = useStyles();

  const primaryNFLTeam = ((player && player.playerNFLTeams) || [])
      .find((nflTeam: NFLTeam) => nflTeam && nflTeam.isPrimary);

  if (!proCombineRatings?.length && !primaryNFLTeam) return null;
  let bestSpeedEvent;
  let bestPowerEvent;
  let bestAgilityEvent;
  if (proCombineRatings?.length) {
    bestSpeedEvent = getTheBestEvent(aggregateAllEventsOfType('speed', proCombineRatings));
    bestPowerEvent = getTheBestEvent(aggregateAllEventsOfType('power', proCombineRatings));
    bestAgilityEvent = getTheBestEvent(aggregateAllEventsOfType('agility', proCombineRatings));
  }
  const height = getMostRecentDataByProperty(nflCombines, 'height') ?? primaryNFLTeam?.height;
  const weight = getMostRecentDataByProperty(nflCombines, 'weight') ?? primaryNFLTeam?.weight;
  const wingspan = getMostRecentDataByProperty(nflCombines, 'wingspan');
  const arm = getMostRecentDataByProperty(nflCombines, 'arm');
  const hand = getMostRecentDataByProperty(nflCombines, 'hand');

  const wingspanPerc = getMostRecentDataByProperty(nflCombines, 'wingspanPerc');
  const heightPerc = getMostRecentDataByProperty(nflCombines, 'heightPerc');
  const weightPerc = getMostRecentDataByProperty(nflCombines, 'weightPerc');
  const armPerc = getMostRecentDataByProperty(nflCombines, 'armPerc');
  const handPerc = getMostRecentDataByProperty(nflCombines, 'handPerc');
  const heightPercentile = height ? heightPerc?.find((hp: any) => hp.code === selectedPosition)?.percentile : undefined;
  const weightPercentile = weight ? weightPerc?.find((wp: any) => wp.code === selectedPosition)?.percentile : undefined;
  const wingspanPercentile = wingspan ? wingspanPerc?.find((wp: any) => wp.code === selectedPosition)?.percentile : undefined;
  const armPercentile = arm ? armPerc?.find((ap: any) => ap.code === selectedPosition)?.percentile : undefined;
  const handPercentile = hand ? handPerc?.find((hp: any) => hp.code === selectedPosition)?.percentile : undefined;
  const noEvents = !bestPowerEvent && !bestSpeedEvent && !bestAgilityEvent;
  const noBiometrics = !height && !weight && !wingspan && !arm && !hand;
  if (noEvents && noBiometrics) return null;

  return (
    <NamedBox
      className={clsx(classes.playerProCombineRating, className)}
      headerNameClassName={classes.headerName}
      name={
        <>
          <div className={classes.headerIcon}>PRO</div>
          <span>{'Pro Combine Rating'}</span>
        </>
      }
    >
      {bestSpeedEvent && (
        <ListItem
          icon={SpeedIcon}
          title='Speed:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{ addSpaceBetweenNumAndAlpha(bestSpeedEvent.name)}</b>
              </div>
            </Grid>
            <Grid item lg xs>
              <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={bestSpeedEvent.percentile}
                    progressBarClassName={classes.progressLineBar}
                  />
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </ListItem>
      )}

      {bestPowerEvent && (
        <ListItem
          icon={PowerIcon}
          title='Power:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{bestPowerEvent.name}</b>
              </div>
            </Grid>
            <Grid item lg xs>
              <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={bestPowerEvent.percentile}
                    progressBarClassName={classes.progressLineBar}
                  />
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </ListItem>
      )}

      {bestAgilityEvent && (
        <ListItem
          icon={AgilityIcon}
          title='Agility:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{bestAgilityEvent.name}</b>
              </div>
            </Grid>
            <Grid item lg xs>
              <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={bestAgilityEvent.percentile}
                    progressBarClassName={classes.progressLineBar}
                  />
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </ListItem>
      )}

      {height && (
        <ListItem
          icon={RulerVerticalIcon}
          title='Height:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{inchesToFeetAndInches(height || 0)}</b>
              </div>
            </Grid>
            <Grid item lg xs>
            {(typeof heightPercentile === 'number') && (
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    progressBarClassName={classes.progressLineBar}
                    value={heightPercentile}
                  />
                </div>
            )}
          </Grid>
          </Grid>
        </ListItem>
      )}

      {weight && (
        <ListItem
          icon={ScaleIcon}
          title='Weight:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{weight} lbs.</b>
              </div>
            </Grid>
            <Grid item lg xs>
            {(typeof weightPercentile === 'number') && (
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    progressBarClassName={classes.progressLineBar}
                    value={weightPercentile}
                  />
                </div>
            )}
          </Grid>
          </Grid>
        </ListItem>
      )}

      {hand && (
        <ListItem
          icon={HandIcon}
          title='Hand:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{hand}</b>
              </div>
            </Grid>
            <Grid item lg xs>
            {(typeof handPercentile === 'number') && (
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    progressBarClassName={classes.progressLineBar}
                    value={handPercentile}
                  />
                </div>
            )}
          </Grid>
          </Grid>
        </ListItem>
      )}

      {arm && (
        <ListItem
          icon={ArmIcon}
          title='Arm:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{arm}</b>
              </div>
            </Grid>
            <Grid item lg xs>
            {(typeof armPercentile === 'number') && (
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    progressBarClassName={classes.progressLineBar}
                    value={armPercentile}
                  />
                </div>
            )}
          </Grid>
          </Grid>
        </ListItem>
      )}

      {wingspan && (
        <ListItem
          icon={DistanceIcon}
          title='Wingspan:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{wingspan}</b>
              </div>
            </Grid>
            <Grid item lg xs>
            {(typeof wingspanPercentile === 'number') && (
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    progressBarClassName={classes.progressLineBar}
                    value={wingspanPercentile}
                  />
                </div>
            )}
          </Grid>
          </Grid>
        </ListItem>
      )}


    </NamedBox>
  );
}
